// src/slices/ticketDesignTemplate/thunk.ts
// Async thunks cho Quản lý Mẫu Thiết kế Vé

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getTicketDesignTemplates,
  getTicketDesignTemplateById,
  getTicketDesignTemplateFileUrl,
  createTicketDesignTemplate as createTemplateAPI,
  updateTicketDesignTemplate as updateTemplateAPI,
  updateTicketDesignTemplateFile as updateTemplateFileAPI,
  updateTicketDesignTemplateLayout as updateTemplateLayoutAPI,
  deleteTicketDesignTemplateFile as deleteTemplateFileAPI,
  deleteTicketDesignTemplate as deleteTemplateAPI,
  approveTicketDesignTemplate as approveTemplateAPI,
  unlockTicketDesignTemplate as unlockTemplateAPI,
} from 'helpers/fakebackend_helper';
import {
  CreateTicketDesignTemplateDto,
  UpdateTicketDesignTemplateDto,
  UpdateTicketDesignTemplateFileDto,
} from 'pages/TicketDesignTemplate/types';

interface GetTemplatesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  designUnit?: string;
  issuancePeriod?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

// Helper function để loại bỏ params null, undefined, hoặc rỗng
const cleanParams = (params: any): any => {
  const cleaned: any = {};
  
  Object.keys(params).forEach((key) => {
    const value = params[key];
    
    // Chỉ thêm vào nếu value không phải null, undefined, hoặc string rỗng
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  });
  
  return cleaned;
};

// Lấy danh sách templates với filters và pagination
export const fetchTicketDesignTemplates = createAsyncThunk(
  'TicketDesignTemplate/fetchTemplates',
  async (params: GetTemplatesParams, { rejectWithValue }) => {
    try {
      // Loại bỏ params null, undefined, hoặc rỗng trước khi gọi API
      const cleanedParams = cleanParams(params);
      const response = await getTicketDesignTemplates(cleanedParams);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi tải danh sách mẫu thiết kế'
      );
    }
  }
);

// Lấy chi tiết template theo ID
export const fetchTicketDesignTemplateById = createAsyncThunk(
  'TicketDesignTemplate/fetchById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getTicketDesignTemplateById(id);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi tải thông tin mẫu thiết kế'
      );
    }
  }
);

// Lấy Signed URL để preview file
export const fetchTicketDesignTemplateFileUrl = createAsyncThunk(
  'TicketDesignTemplate/fetchFileUrl',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getTicketDesignTemplateFileUrl(id);
      // Backend trả về string URL trực tiếp
      const payload = response.data || response;
      return payload?.fileUrl || payload?.url || payload;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi tải URL file'
      );
    }
  }
);

// Tạo mới template
export const createTicketDesignTemplate = createAsyncThunk(
  'TicketDesignTemplate/create',
  async (data: CreateTicketDesignTemplateDto | FormData, { rejectWithValue }) => {
    try {
      const response = await createTemplateAPI(data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi tạo mẫu thiết kế'
      );
    }
  }
);

// Cập nhật template (chỉ khi status = Pending)
export const updateTicketDesignTemplate = createAsyncThunk(
  'TicketDesignTemplate/update',
  async (data: UpdateTicketDesignTemplateDto, { rejectWithValue }) => {
    try {
      const response = await updateTemplateAPI(data.id, data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi cập nhật mẫu thiết kế'
      );
    }
  }
);

export const updateTicketDesignTemplateFile = createAsyncThunk(
  'TicketDesignTemplate/updateFile',
  async (data: UpdateTicketDesignTemplateFileDto, { rejectWithValue }) => {
    try {
      const response = await updateTemplateFileAPI(data.id, data.file);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi cập nhật file mẫu thiết kế'
      );
    }
  }
);

// Lưu vị trí/kích thước các phần tử overlay (mã vé, số vé, QR, ngày mở thưởng...) lên mẫu vé
export const updateTicketDesignTemplateLayout = createAsyncThunk(
  'TicketDesignTemplate/updateLayout',
  async (data: { id: number; layoutJson: string | null }, { rejectWithValue }) => {
    try {
      const response = await updateTemplateLayoutAPI(data.id, data.layoutJson);
      return { id: data.id, layoutJson: data.layoutJson, response: response.data || response };
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi lưu bố cục mẫu vé'
      );
    }
  }
);

export const deleteTicketDesignTemplateFile = createAsyncThunk(
  'TicketDesignTemplate/deleteFile',
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteTemplateFileAPI(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi xóa file mẫu thiết kế'
      );
    }
  }
);

// Xóa template (chỉ khi status = Pending)
export const deleteTicketDesignTemplate = createAsyncThunk(
  'TicketDesignTemplate/delete',
  async (id: number, { rejectWithValue }) => {
    try {
      await deleteTemplateAPI(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi xóa mẫu thiết kế'
      );
    }
  }
);

// Phê duyệt template (chỉ khi status = Pending)
export const approveTicketDesignTemplate = createAsyncThunk(
  'TicketDesignTemplate/approve',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await approveTemplateAPI(id);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi phê duyệt mẫu thiết kế'
      );
    }
  }
);

export const unlockTicketDesignTemplate = createAsyncThunk(
  'TicketDesignTemplate/unlock',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await unlockTemplateAPI(id);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(
        error?.message || 'Có lỗi xảy ra khi mở khóa mẫu thiết kế'
      );
    }
  }
);
